import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { RewardsService } from '../../../../core/services/rewards.service';
import { NotifierService, Util } from '@wm-libs/core';
import { ModifierRewards, Reward, Rewards } from '../../../../core/interfaces/rewards.interface';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule } from '@ngx-translate/core';
import { EmptyDataComponent } from '../../../../core/components/empty-data/empty-data.component';
import { DataTableComponent } from '../../../../core/components/data-table/data-table';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgClass, NgIf, NgFor, AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-rewards',
  templateUrl: './rewards.component.html',
  styleUrls: ['./rewards.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    MatDatepickerModule,
    MatButtonModule,
    DataTableComponent,
    EmptyDataComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class RewardsComponent implements OnInit, OnDestroy {
  filtersForm!: FormGroup;

  initRewards: Rewards | null = null;
  rewards: ModifierRewards | null = null;

  firstIncomeDate!: Date | number;
  lastIncomeDate!: Date | number;

  displayedColumns = ['timestamp', 'currency', 'totalProfit', 'pplnsProfit', 'ppsProfit', 'totalHashrate'];
  typeFilters = [
    { value: 'any', label: 'REWARDS.FILTER.ANY' },
    { value: 'pplns', label: 'REWARDS.FILTER.PPLNS' },
    { value: 'pps', label: 'REWARDS.FILTER.PPS' },
  ];
  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));

  // pagination
  pagination = {
    page: 0,
    count: 10,
  };

  private _unsubscribe$: Subject<void> = new Subject<void>();
  initRewardsLoaded = false;
  rewardsLoaded = false;

  isFormRessetingProcess = false;

  constructor(private formBuilder: FormBuilder, private rewardsService: RewardsService, private breakpointObserver: BreakpointObserver, private notifier: NotifierService) {
    this.filtersForm = this.formBuilder.group({
      type: ['any'],
      startTime: [null, [Validators.required]],
      endTime: [null, [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.notifier.notifications$.pipe(takeUntil(this._unsubscribe$)).subscribe((result) => {
      if (result === 'POOL.FARM_SWITCHED') {
        this.notifier.clearNotifications();
        this.initRewards = null;
        this.initRewardsLoaded = false;

        this.resetParams();
        this.getRewards();
      }
    });

    this.filtersForm.valueChanges.pipe(takeUntil(this._unsubscribe$)).subscribe((res) => {
      this.pagination.page = 0;

      if (res.type !== 'any') {
        this.displayedColumns = ['timestamp', 'currency', `${res.type}Profit`];
      } else {
        this.displayedColumns = ['timestamp', 'currency', 'totalProfit', 'pplnsProfit', 'ppsProfit', 'totalHashrate'];
      }
      this.getRewards();
    });

    this.getRewards();
  }

  getRewards() {
    if (this.isFormRessetingProcess) {
      return;
    }

    this.rewards = null;
    this.rewardsLoaded = false;

    const { type, startTime, endTime } = this.filtersForm.value;

    let startTimestamp;
    let endTimestamp;

    if (startTime && endTime) {
      startTimestamp = Util.convertDateToTimestamp(startTime, 'startOfDay');
      endTimestamp = Util.convertDateToTimestamp(endTime, 'endOfDay');
    }

    const finalType = type && type !== 'any' ? type : undefined;

    const params = {
      from: this.pagination.page,
      count: this.pagination.count,
      type: finalType,
      startTime: startTimestamp,
      endTime: endTimestamp,
    };

    this.rewardsService.getRewards(params).subscribe((res) => {
      if (this.initRewardsLoaded === false) {
        this.initRewardsLoaded = true;
        this.initRewards = res;

        this.firstIncomeDate = this.initRewards?.firstIncomeDate ? Util.convertTimestampToDate(this.initRewards?.firstIncomeDate) : Date.now();
        this.lastIncomeDate = this.initRewards?.lastIncomeDate ? Util.convertTimestampToDate(this.initRewards?.lastIncomeDate) : Date.now();

        this.isFormRessetingProcess = true;
        this.filtersForm.patchValue({
          startTime: this.firstIncomeDate,
          endTime: this.lastIncomeDate,
        });
        this.isFormRessetingProcess = false;
      }

      this.rewardsLoaded = true;
      this.updateData(res);
    });
  }

  pageChange(event: PageEvent) {
    this.pagination.count = event.pageSize;
    this.pagination.page = event.pageIndex * this.pagination.count;

    this.getRewards();
  }

  resetParams(): void {
    this.isFormRessetingProcess = true;
    this.displayedColumns = ['timestamp', 'currency', 'totalProfit', 'pplnsProfit', 'ppsProfit', 'totalHashrate'];

    this.pagination.count = 10;
    this.pagination.page = 0;

    this.filtersForm.setValue(
      {
        type: 'any',
        startTime: null,
        endTime: null,
      },
      { emitEvent: false }
    );
    this.isFormRessetingProcess = false;
  }

  updateData(result: Rewards) {
    this.rewards = {
      total: result?.total,
      count: result?.count,
      firstIncomeDate: result?.firstIncomeDate,
      lastIncomeDate: result?.lastIncomeDate,
      data: result?.data?.map((item: Reward) => ({
        ...item,
        timestamp: Util.formatDateWithoutHours(new Date(Number(item.timestamp) * 1000)),
        totalHashrate: item.totalHashrate + item.totalHashrateSymbol,
      })),
    };
  }

  exportRewards() {
    const { type, startTime, endTime } = this.filtersForm.value;
    const finalType = type && type !== 'any' ? type : undefined;

    this.rewardsService.exportRewards(finalType, startTime, endTime);
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
